// Session keep-alive — the user-configurable "stay signed in" behaviour.
//
// The platform issues 15-minute access cookies with a longer httpOnly refresh
// cookie. Left alone, a quiet tab dies at the first 401 after 15 minutes even
// though the person is still working. This module keeps the session alive while
// they are actually active (proactive refresh well before expiry, plus a
// refresh on tab-wake after laptop sleep) and, when they opt in, signs them out
// after a chosen period of inactivity.
//
// The preference is per-user, stored in auth-service user preferences
// (userMetadata.session) so it follows them across KaziPeople apps, with a
// local cache for instant startup:
//   { keepActive: boolean, idleTimeoutMinutes: number }   // 0 = never
//
// Ported verbatim across main-app / admin-portal / partner-portal (same
// pattern as ThemeProvider); only the start() wiring differs per app.

export interface SessionPrefs {
  keepActive: boolean;
  idleTimeoutMinutes: number;
}

export const DEFAULT_SESSION_PREFS: SessionPrefs = { keepActive: true, idleTimeoutMinutes: 0 };
// 0 = never sign me out for inactivity.
export const IDLE_TIMEOUT_CHOICES = [0, 15, 30, 60, 240];

const STORE_KEY = 'kazi-session-prefs';
const ORG_POLICY_KEY = 'kazi-org-session-policy';

// ── Org session policy (Organization Settings → Security) ────────────────────
// The org's "Automatic session timeout" toggle previously saved a flag nothing
// read. When enforced, the ORG timeout applies to every member: the effective
// idle timeout is the org limit, tightened further by a user who chose an even
// SHORTER personal timeout — a user preference can never loosen org policy.
export interface OrgSessionPolicy {
  sessionTimeoutEnforced: boolean;
  sessionTimeoutMinutes: number;
}

let orgPolicy: OrgSessionPolicy | null = null;
try {
  const raw = localStorage.getItem(ORG_POLICY_KEY);
  if (raw) orgPolicy = JSON.parse(raw);
} catch {
  /* defaults apply until the fetch lands */
}

export function setOrgSessionPolicy(p: OrgSessionPolicy | null): void {
  orgPolicy = p;
  try {
    if (p) localStorage.setItem(ORG_POLICY_KEY, JSON.stringify(p));
    else localStorage.removeItem(ORG_POLICY_KEY);
  } catch {
    /* in-memory copy still applies this session */
  }
}

/** The idle timeout actually in force: org policy (when enforced) capped by a
 *  stricter personal choice; otherwise the personal preference alone. */
export function effectiveIdleTimeoutMinutes(userMinutes: number): number {
  const org = orgPolicy;
  if (!org?.sessionTimeoutEnforced) return userMinutes;
  const orgMin = Number(org.sessionTimeoutMinutes) >= 5 ? Number(org.sessionTimeoutMinutes) : 30;
  if (userMinutes > 0) return Math.min(userMinutes, orgMin);
  return orgMin;
}

export function normalizeSessionPrefs(raw: unknown): SessionPrefs {
  const r = (raw ?? {}) as Record<string, unknown>;
  const n = Number(r.idleTimeoutMinutes);
  return {
    keepActive: r.keepActive !== false,
    idleTimeoutMinutes: IDLE_TIMEOUT_CHOICES.includes(n) ? n : 0,
  };
}

export function getSessionPrefs(): SessionPrefs {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return normalizeSessionPrefs(JSON.parse(raw));
  } catch {
    /* fall through to defaults */
  }
  return { ...DEFAULT_SESSION_PREFS };
}

export function setSessionPrefs(p: SessionPrefs): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(normalizeSessionPrefs(p)));
  } catch {
    /* private mode — keep-alive still runs with in-memory defaults */
  }
}

const TICK_MS = 30_000;
// Access tokens live 15m — refreshing at 10m keeps every API client (including
// ones without their own 401-retry) inside a valid window.
const REFRESH_EVERY_MS = 10 * 60_000;
// "Active" = interacted within the last 5 minutes.
const ACTIVE_WINDOW_MS = 5 * 60_000;

export function startSessionKeepAlive(opts: {
  /** POST the app's silent refresh endpoint. Failures are ignored here — the reactive 401 path owns a dead session. */
  refresh: () => Promise<unknown>;
  /** Called once when the user's idle timeout elapses. */
  signOut: () => void;
  /** Optional gate so the loop no-ops while signed out. Defaults to always-on. */
  isAuthed?: () => boolean;
}): () => void {
  let lastActivity = Date.now();
  let lastRefresh = Date.now();
  let stopped = false;

  const activity = () => {
    lastActivity = Date.now();
  };
  const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
  events.forEach((e) => window.addEventListener(e, activity, { passive: true }));

  const doRefresh = () => {
    lastRefresh = Date.now();
    void Promise.resolve(opts.refresh()).catch(() => undefined);
  };

  // Laptop-sleep / background-tab wake: the access cookie may already be past
  // its 15m — refresh immediately so the first click doesn't 401.
  const onVisible = () => {
    if (document.visibilityState !== 'visible' || stopped) return;
    activity();
    if (!(opts.isAuthed?.() ?? true)) return;
    if (getSessionPrefs().keepActive && Date.now() - lastRefresh >= REFRESH_EVERY_MS) doRefresh();
  };
  document.addEventListener('visibilitychange', onVisible);

  const timer = window.setInterval(() => {
    if (stopped || !(opts.isAuthed?.() ?? true)) return;
    const prefs = getSessionPrefs();
    const idle = Date.now() - lastActivity;
    const timeoutMinutes = effectiveIdleTimeoutMinutes(prefs.idleTimeoutMinutes);
    if (timeoutMinutes > 0 && idle >= timeoutMinutes * 60_000) {
      stop();
      opts.signOut();
      return;
    }
    if (
      prefs.keepActive &&
      document.visibilityState === 'visible' &&
      idle < ACTIVE_WINDOW_MS &&
      Date.now() - lastRefresh >= REFRESH_EVERY_MS
    ) {
      doRefresh();
    }
  }, TICK_MS);

  function stop() {
    if (stopped) return;
    stopped = true;
    window.clearInterval(timer);
    events.forEach((e) => window.removeEventListener(e, activity));
    document.removeEventListener('visibilitychange', onVisible);
  }
  return stop;
}
